import { useEffect, useState } from "react";
import { ChevronDown, MapPin } from "lucide-react";

/**
 * PopularLoganRoutesHint — appears on /book only when the pickup OR dropoff
 * field contains "Logan" (case-insensitive). Lets the user autofill one of
 * 5 curated routes with a single tap. Marketing sedan ranges come from
 * /api/pricing/marketing (display-only) — final quote resolves at booking.
 */
const API = process.env.REACT_APP_BACKEND_URL;

// Curated slice — the 5 most-searched Logan pickups from the marketing rate matrix.
const SLICE = [
  { key: "Downtown Boston", pickup: "Downtown Boston / Seaport",         region: "Core Boston" },
  { key: "Cambridge",       pickup: "Cambridge / Somerville",            region: "Core Boston" },
  { key: "Newton",          pickup: "Newton / Chestnut Hill",            region: "MetroWest & Route 9 Corridor" },
  { key: "Quincy",          pickup: "Quincy / Milton / Braintree",       region: "South Shore" },
  { key: "Wellesley",       pickup: "Wellesley / Needham",               region: "MetroWest & Route 9 Corridor" },
];

function looksLikeLogan(v) {
  return typeof v === "string" && v.toLowerCase().includes("logan");
}

export default function PopularLoganRoutesHint({
  pickupValue,
  dropoffValue,
  onApply,
}) {
  const [expanded, setExpanded] = useState(false);
  const [rows, setRows] = useState([]);

  const active = looksLikeLogan(pickupValue) || looksLikeLogan(dropoffValue);

  useEffect(() => {
    if (!active || rows.length) return;
    let cancelled = false;
    fetch(`${API}/api/pricing/marketing`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        const flat = [];
        for (const [region, list] of Object.entries(d.logan_routes || {})) {
          for (const row of list) flat.push({ ...row, region });
        }
        const enriched = SLICE.map((s) => {
          const match = flat.find((f) => f.pickup === s.pickup);
          return match ? { ...s, sedan: match.sedan, suv: match.suv, van: match.van } : s;
        });
        setRows(enriched);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [active, rows.length]);

  if (!active) return null;

  return (
    <section
      data-testid="popular-logan-hint"
      className="backdrop-blur-xl bg-[#121214]/70 border border-[#C5A24A]/25 mb-5"
    >
      <button
        type="button"
        aria-expanded={expanded}
        data-testid="popular-logan-hint-toggle"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 md:px-6 py-4 text-left hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-3">
          <MapPin size={16} strokeWidth={1.5} className="text-[#C5A24A]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#C5A24A]">
              Logan Airport detected
            </div>
            <div className="text-sm text-white mt-0.5">Popular Logan Routes</div>
          </div>
        </div>
        <ChevronDown
          size={18}
          strokeWidth={1.5}
          className={`text-[#8E9AA6] transition-transform duration-300 ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {expanded && (
        <ul
          data-testid="popular-logan-hint-list"
          className="divide-y divide-white/[0.06] border-t border-white/[0.06]"
        >
          {(rows.length ? rows : SLICE).map((r) => (
            <li
              key={r.key}
              data-testid={`popular-logan-hint-row-${r.key.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 px-5 md:px-6 py-4"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white leading-snug">{r.pickup} ↔ Logan</div>
                <div className="text-[11px] uppercase tracking-[0.24em] text-[#8E9AA6] mt-1">
                  {r.region}
                </div>
              </div>
              <div className="text-sm text-[#E6E6E6] tabular-nums font-light">
                Sedan {r.sedan || "—"}
              </div>
              <button
                type="button"
                onClick={() =>
                  onApply?.({
                    pickup: `${r.pickup}, MA`,
                    dropoff: "Boston Logan International Airport (BOS)",
                  })
                }
                data-testid={`popular-logan-hint-apply-${r.key.toLowerCase().replace(/\s+/g, "-")}`}
                className="shrink-0 text-[10px] uppercase tracking-[0.28em] text-[#C5A24A] hover:text-white border border-[#C5A24A]/50 hover:border-white px-4 py-2 transition-colors"
              >
                Use this route
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
